import Cookies from 'js-cookie';
import find from 'lodash/find';
import { ApiError, getJSON, RSAA } from 'redux-api-middleware';
import { EventTypes } from 'redux-segment';

import { getCookieDomain } from '../utils';

const CREATE = 'formlabs/cart/CREATE';
const CREATE_SUCCESS = 'formlabs/cart/CREATE_SUCCESS';
const CREATE_FAILURE = 'formlabs/cart/CREATE_FAILURE';

const ADD = 'formlabs/cart/ADD';
const ADD_SUCCESS = 'formlabs/cart/ADD_SUCCESS';
const ADD_FAILURE = 'formlabs/cart/ADD_FAILURE';

const UPDATE = 'formlabs/cart/UPDATE';
const UPDATE_SUCCESS = 'formlabs/cart/UPDATE_SUCCESS';
const UPDATE_FAILURE = 'formlabs/cart/UPDATE_FAILURE';

const UPDATE_ITEMS_SUCCESS = 'formlabs/cart/UPDATE_ITEMS_SUCCESS';

const UPDATE_ITEM = 'formlabs/cart/UPDATE_ITEM';
const UPDATE_ITEM_SUCCESS = 'formlabs/cart/UPDATE_ITEM_SUCCESS';
const UPDATE_ITEM_FAILURE = 'formlabs/cart/UPDATE_ITEM_FAILURE';

const REMOVE = 'formlabs/cart/REMOVE';
const REMOVE_SUCCESS = 'formlabs/cart/REMOVE_SUCCESS';
const REMOVE_FAILURE = 'formlabs/cart/REMOVE_FAILURE';

const RESET = 'formlabs/cart/RESET';

const createApiUrl = (storeId, endpoint) =>
  `${process.env.RAZZLE_MAGENTO_URL}/rest/${storeId}/V1/${endpoint}`;

const apiOptions = {
  credentials: 'include',
  headers: {
    accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
};

const initialState = {
  id: null,
  items: [],
  loading: false,
  error: null,
  totals: null,
  cartUpdating: false
};

export const cartTrackMappings = {
  [ADD_SUCCESS]: (getState, action) => {
    const { cart, router, store } = getState();
    const {
      payload: { items, tracking }
    } = action;

    const mapBundleOptions = options => {
      const result = [];
      for (let option of options.values()) {
        const entries = [];
        for (let [key, entry] of option.entries()) {
          entries.push({
            sku: key,
            name: entry.product.name,
            price: entry.price,
            qty: entry.qty
          });
        }
        result.push(entries);
      }
      return result;
    };

    return {
      eventType: EventTypes.track,
      eventPayload: {
        event: 'Product Added',
        properties: {
          category: 'Storefront',
          context: tracking.context,
          id: items.item_id,
          name: items.name,
          path: router.location.pathname,
          price: items.price,
          quantity: tracking.cartItem.qty,
          sku: items.sku,
          url: window.location.href,
          cart: cart.id,
          store: store.id,
          options: tracking.bundleOptions
            ? mapBundleOptions(tracking.bundleOptions)
            : null
        }
      }
    };
  },
  [REMOVE_SUCCESS]: (getState, action) => {
    const { cart, router, store } = getState();
    return {
      eventType: EventTypes.track,
      eventPayload: {
        event: 'Product Removed',
        properties: {
          category: 'Storefront',
          context: 'cart',
          id: action.payload.itemId,
          path: router.location.pathname,
          url: window.location.href,
          cart: cart.id,
          store: store.id
        }
      }
    };
  }
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case CREATE:
      return { ...state, loading: true };
    case CREATE_SUCCESS:
      return {
        ...state,
        loading: false,
        id: action.payload
      };
    case CREATE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.message
      };
    case ADD:
      return { ...state, loading: action.payload.sku };
    case ADD_SUCCESS:
      return {
        ...state,
        loading: false,
        items: [...state.items, action.payload.items]
      };
    case ADD_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.response
          ? action.payload.response.message
          : action.payload.message
      };
    case UPDATE:
      return { ...state, cartUpdating: true };
    case UPDATE_SUCCESS:
      return {
        ...state,
        cartUpdating: false,
        totals: action.payload
      };
    case UPDATE_FAILURE:
      return {
        ...state,
        cartUpdating: false,
        error: action.payload.response
          ? action.payload.response.message
          : action.payload.message
      };
    case UPDATE_ITEMS_SUCCESS:
      return {
        ...state,
        cartUpdating: false,
        items: action.payload.items
      };
    case UPDATE_ITEM:
      return { ...state, cartUpdating: true };
    case UPDATE_ITEM_SUCCESS:
      return {
        ...state,
        cartUpdating: false // FIXME update item
      };
    case UPDATE_ITEM_FAILURE:
      return {
        ...state,
        cartUpdating: false,
        error: action.payload.response
          ? action.payload.response.message
          : action.payload.message
      };
    case REMOVE:
      return { ...state, cartUpdating: true };
    case REMOVE_SUCCESS:
      return {
        ...state,
        cartUpdating: false
      };
    case REMOVE_FAILURE:
      return {
        ...state,
        cartUpdating: false,
        error: action.payload.response
          ? action.payload.response.message
          : action.payload.message
      };

    case RESET:
      return initialState;
    default:
      return state;
  }
}

export const resetCart = () => {
  Cookies.remove('cart', { domain: getCookieDomain() });
  return {
    type: RESET
  };
};

export const setCartId = cartId => {
  return {
    type: CREATE_SUCCESS,
    payload: cartId
  };
};

export const createCart = () => {
  return async (dispatch, getState) => {
    // bail out on missing store id
    if (!getState().store.id) {
      return;
    }

    const cartId = Cookies.get('cart');

    // Regenerate cartId from cookie and fetch cart
    if (cartId) {
      await dispatch(setCartId(cartId));
      await dispatch(updateCart());
      return;
    }

    return await dispatch({
      [RSAA]: {
        ...apiOptions,
        types: [
          CREATE,
          {
            type: CREATE_SUCCESS,
            payload: async (action, state, res) => {
              const result = await getJSON(res);
              Cookies.set('cart', result, { domain: getCookieDomain() });
              return result;
            }
          },
          CREATE_FAILURE
        ],
        endpoint: state => {
          const url = createApiUrl(
            state.store.id,
            state.auth.isAuthenticated ? 'carts/mine' : 'guest-carts'
          );
          return url;
        },

        method: 'POST'
      }
    });
  };
};

export const addToCart = (
  cartItem,
  bundleOptions = null,
  context = '',
  callback = null
) => {
  return async (dispatch, getState) => {
    const { cart } = getState();

    if (!cart.id) {
      const createCartActionResponse = await dispatch(createCart());
      if (createCartActionResponse && createCartActionResponse.error) {
        throw new Error('Unable to create cart.', createCartActionResponse);
      }
    }

    await dispatch({
      [RSAA]: {
        ...apiOptions,
        types: [
          { type: ADD, payload: { sku: cartItem.sku } },
          {
            type: ADD_SUCCESS,
            payload: async (action, state, res) => {
              const result = await getJSON(res);
              if (callback) {
                callback();
              }
              dispatch(updateCart());
              return {
                items: result,
                tracking: {
                  cartItem,
                  bundleOptions,
                  context
                }
              };
            }
          },
          {
            type: ADD_FAILURE,
            payload: async (action, state, res) => {
              const json = await getJSON(res);
              const result = new ApiError(res.status, res.statusText, json);
              if (callback) {
                callback(result);
              }
              return result;
            }
          }
        ],
        body: state => {
          const content = {
            cartItem: {
              ...cartItem,
              quote_id: state.cart.id
            }
          };

          if (bundleOptions && bundleOptions.size) {
            const bundle_options = [];

            for (var [key, value] of bundleOptions) {
              const option = {
                option_id: key,
                option_qty: 1,
                option_selections: Array.from(value.values()).map(
                  entry => entry.id
                )
              };
              bundle_options.push(option);
            }

            content.cartItem.product_option = {
              extension_attributes: {
                bundle_options
              }
            };
          }

          return JSON.stringify(content);
        },
        endpoint: state =>
          createApiUrl(
            state.store.id,
            state.auth.isAuthenticated
              ? 'carts/mine/items'
              : `guest-carts/${state.cart.id}/items`
          ),
        method: 'POST'
      }
    });
  };
};

export const updateItems = () => ({
  [RSAA]: {
    ...apiOptions,
    types: [UPDATE, UPDATE_ITEMS_SUCCESS, UPDATE_FAILURE],
    endpoint: state =>
      createApiUrl(
        state.store.id,
        state.auth.isAuthenticated
          ? 'carts/mine'
          : `guest-carts/${state.cart.id}`
      ),
    method: 'GET'
  }
});

export const updateCart = () => {
  return async dispatch => {
    await dispatch({
      [RSAA]: {
        ...apiOptions,
        types: [UPDATE, UPDATE_SUCCESS, UPDATE_FAILURE],
        endpoint: state =>
          createApiUrl(
            state.store.id,
            state.auth.isAuthenticated
              ? 'carts/mine/totals'
              : `guest-carts/${state.cart.id}/totals`
          ),
        method: 'GET'
      }
    });

    await dispatch(updateItems());
  };
};

export const removeItem = itemId => {
  return async dispatch => {
    await dispatch({
      [RSAA]: {
        ...apiOptions,
        types: [
          REMOVE,
          {
            type: REMOVE_SUCCESS,
            payload: {
              tracking: {
                itemId
              }
            }
          },
          REMOVE_FAILURE
        ],
        endpoint: state =>
          createApiUrl(
            state.store.id,
            state.auth.isAuthenticated
              ? `carts/mine/items/${itemId}`
              : `guest-carts/${state.cart.id}/items/${itemId}`
          ),
        method: 'DELETE'
      }
    });

    await dispatch(updateCart());
  };
};

export const updateItem = (itemId, key, value) => {
  return async (dispatch, getState) => {
    await dispatch({
      [RSAA]: {
        ...apiOptions,
        types: [UPDATE_ITEM, UPDATE_ITEM_SUCCESS, UPDATE_ITEM_FAILURE],
        body: state => {
          const cartItem = find(state.cart.items, { item_id: itemId });
          const content = {
            cart_item: {
              ...cartItem,
              quote_id: state.cart.id,
              [key]: value
            }
          };

          return JSON.stringify(content);
        },
        endpoint: state =>
          createApiUrl(
            state.store.id,
            state.auth.isAuthenticated
              ? `carts/mine/items/${itemId}`
              : `guest-carts/${state.cart.id}/items/${itemId}`
          ),
        method: 'PUT'
      }
    });

    await dispatch(updateCart());
  };
};

export const checkSession = () => {
  return async (dispatch, getState) => {
    const {
      auth: { isAuthenticated },
      cart: { id: cartId }
    } = getState();

    const resetCart = async () => {
      Cookies.remove('cart', { domain: getCookieDomain() });
      return await dispatch(createCart());
    };

    const flpcCookie = Cookies.get('flpc');
    const cartCookie = Cookies.get('cart');

    // Create cart if none exists or regenerate from cookie
    if (
      (isAuthenticated && flpcCookie && !cartCookie) ||
      (cartCookie && !cartId)
    ) {
      return await dispatch(createCart());
    }

    // Reset cart after user logout
    if (isAuthenticated && !flpcCookie && cartCookie) {
      Cookies.remove('cart', { domain: getCookieDomain() });
      window.location.reload();
    }

    // Reset cart after user login
    if (!isAuthenticated && flpcCookie && cartCookie) {
      resetCart();
    }

    // Quickfix: if the quote id does not match its expected structure delete the cart
    // guest quote id is a hash with the length of 32
    // customer quote id is a autoincrement integer
    if (flpcCookie && cartCookie && cartCookie.length === 32) {
      resetCart();
    }

    // Fresh visitor, create a new cart
    if (!isAuthenticated && !flpcCookie && !cartCookie) {
      return await dispatch(createCart());
    }

    // Loggedin visitor, has cart. Fetch cart.
    return await dispatch(updateCart());
  };
};
